/**
 * Adaptador YouTube (IFrame Player API), incluidos DIRECTOS.
 *
 * Es el caso de uso principal de webinars: la landing embebe un
 * `<iframe src="https://www.youtube.com/embed/...">` (o su variante
 * -nocookie) y aquí se le engancha la API oficial para saber play/pausa/
 * posición sin que el cliente toque una línea de JS.
 *
 * Dos cosas que YouTube exige y que hacemos automáticamente:
 *   1. `enablejsapi=1` en la URL del iframe — si falta, reescribimos el src
 *      (recarga el player; por eso se hace en el arranque, antes de que
 *      nadie le haya dado al play).
 *   2. `origin` con el origen de la página, o el player ignora la API.
 *
 * En un directo `getDuration()` devuelve 0 o la ventana de DVR y el porcentaje
 * no significa nada: la sesión se marca `live` y la métrica pasa a ser el
 * tiempo de permanencia (elapsed/active) + los hitos de minutos.
 */
import { VideoSession, observeVisibility, config } from './core';

type YTPlayer = {
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  getVideoData: () => { video_id?: string; title?: string; author?: string; isLive?: boolean };
  getPlaybackRate: () => number;
  getPlaybackQuality: () => string;
  isMuted: () => boolean;
  getVideoUrl: () => string;
};

type YTEvent = { target: YTPlayer; data: number };

type YTApi = {
  Player: new (
    el: Element | string,
    opts: { events?: Record<string, (e: YTEvent) => void> },
  ) => YTPlayer;
};

declare global {
  interface Window {
    YT?: YTApi & { loaded?: number };
    onYouTubeIframeAPIReady?: () => void;
  }
}

// Estados del player de YouTube.
const ENDED = 0;
const PLAYING = 1;
const PAUSED = 2;
const BUFFERING = 3;

const YT_EMBED = /(?:youtube\.com|youtube-nocookie\.com)\/embed\/([\w-]{6,})/i;

type IframeWithState = HTMLIFrameElement & { __trkVideo?: VideoSession };

let apiLoading = false;
const pendingIframes: HTMLIFrameElement[] = [];

/** Carga la IFrame API una sola vez y sin pisar el hook del cliente. */
function loadApi(): void {
  if (typeof window === 'undefined') return;
  if (window.YT && window.YT.Player) { drainPending(); return; }
  if (apiLoading) return;
  apiLoading = true;

  const prev = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    try { prev?.(); } catch { /* el hook del cliente no es cosa nuestra */ }
    drainPending();
  };

  // Si el cliente ya insertó el script (VSL con su propio código), no lo
  // duplicamos: basta con habernos encadenado al callback de arriba.
  const already = document.querySelector('script[src*="youtube.com/iframe_api"], script[src*="youtube.com/player_api"]');
  if (already) return;

  const s = document.createElement('script');
  s.src = 'https://www.youtube.com/iframe_api';
  s.async = true;
  (document.head || document.documentElement).appendChild(s);
}

function drainPending(): void {
  // Snapshot: `bind` vuelve a encolar si la API aún no está y un while sobre
  // el array vivo no terminaría nunca.
  const batch = pendingIframes.splice(0, pendingIframes.length);
  for (const el of batch) bind(el);
}

/** Asegura enablejsapi=1 + origin. Devuelve true si hubo que recargar. */
function ensureJsApi(iframe: HTMLIFrameElement): boolean {
  const raw = iframe.getAttribute('src') || '';
  if (!raw) return false;
  let url: URL;
  try {
    url = new URL(raw, location.href);
  } catch {
    return false;
  }
  const hadApi = url.searchParams.get('enablejsapi') === '1';
  const hadOrigin = !!url.searchParams.get('origin');
  if (hadApi && hadOrigin) return false;
  url.searchParams.set('enablejsapi', '1');
  url.searchParams.set('origin', location.origin);
  iframe.setAttribute('src', url.toString());
  return true;
}

function videoIdFrom(iframe: HTMLIFrameElement): string | null {
  const m = YT_EMBED.exec(iframe.getAttribute('src') || '');
  return m ? m[1] : null;
}

function bind(iframe: HTMLIFrameElement): void {
  const target = iframe as IframeWithState;
  if (target.__trkVideo) return;
  if (!window.YT || !window.YT.Player) {
    if (pendingIframes.indexOf(iframe) < 0) pendingIframes.push(iframe);
    return;
  }

  const ytId = videoIdFrom(iframe);
  const id = iframe.dataset.videoId || (ytId ? `yt:${ytId}` : `yt-${Math.random().toString(36).slice(2, 8)}`);

  const session = new VideoSession({
    id,
    title: iframe.dataset.videoTitle || iframe.title || null,
    src: iframe.getAttribute('src'),
    player: 'youtube',
    live: iframe.dataset.videoLive === 'true' || iframe.hasAttribute('data-video-live'),
    webinarId: iframe.dataset.webinarId || null,
    startsAt: iframe.dataset.webinarStartsAt ? Date.parse(iframe.dataset.webinarStartsAt) || null : null,
    el: iframe,
  });
  target.__trkVideo = session;
  observeVisibility(session);

  let player: YTPlayer | null = null;
  let state = -1;

  session.probe = () => {
    if (!player) return null;
    try {
      // BUFFERING no cuenta como reproducción (spinner girando ≠ visionado).
      return { position: player.getCurrentTime(), playing: state === PLAYING };
    } catch {
      return null;
    }
  };

  const readMeta = (p: YTPlayer) => {
    try {
      const data = p.getVideoData();
      if (data?.title && !session.title) session.title = data.title;
      const dur = p.getDuration();
      // isLive del propio player; duración 0 con reproducción en marcha es el
      // otro síntoma de directo (emisiones sin DVR).
      if (data?.isLive === true) session.live = true;
      if (dur > 0) session.setDuration(dur);
      else if (state === PLAYING) session.live = true;
      session.setQuality(p.getPlaybackQuality?.() ?? null);
      session.muted = p.isMuted?.() ?? false;
      session.rate = p.getPlaybackRate?.() ?? 1;
    } catch {
      /* la API tarda un tick en responder tras onReady */
    }
  };

  try {
    // eslint-disable-next-line new-cap
    player = new window.YT.Player(iframe, {
      events: {
        onReady: (e: YTEvent) => {
          player = e.target;
          readMeta(e.target);
          session.impression();
        },
        onStateChange: (e: YTEvent) => {
          const prev = state;
          state = e.data;
          player = e.target;
          const pos = (() => { try { return e.target.getCurrentTime(); } catch { return session.position; } })();
          if (state === PLAYING) {
            readMeta(e.target);
            session.play(pos);
          } else if (state === PAUSED) {
            session.pause(pos);
          } else if (state === ENDED) {
            session.ended();
          } else if (state === BUFFERING && prev === PLAYING) {
            // Buffering tras un salto: cortamos la acumulación de contenido.
            session.tick({ position: pos, playing: false });
          }
        },
        onPlaybackRateChange: (e: YTEvent) => session.setRate(Number(e.data) || 1),
        onPlaybackQualityChange: (e: YTEvent) => session.setQuality(String(e.data)),
        onError: (e: YTEvent) => session.error(e.data),
      },
    });
  } catch (err) {
    if (config.debug) console.warn('[trackrift-video] YT.Player falló:', err);
    return;
  }

  if (config.debug) console.debug('[trackrift-video] iframe YouTube instrumentado:', id);
}

/** Instrumenta los iframes de YouTube bajo `root`. Devuelve cuántos nuevos. */
export function scanYouTube(root: ParentNode = document): number {
  if (typeof window === 'undefined') return 0;
  const frames = Array.from(root.querySelectorAll?.('iframe') ?? []) as HTMLIFrameElement[];
  let n = 0;
  for (const f of frames) {
    if (f.hasAttribute('data-track-ignore')) continue;
    if (!YT_EMBED.test(f.getAttribute('src') || '')) continue;
    if ((f as IframeWithState).__trkVideo) continue;
    ensureJsApi(f);
    pendingIframes.push(f);
    n += 1;
  }
  if (n > 0) loadApi();
  else if (pendingIframes.length) loadApi();
  return n;
}
